import { useState } from "react";
import { Gift, Search, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/api";

const QUICK = [5, 10, 25, 50];

const fmtPhone = (p) =>
  p && p.length === 10 ? `(${p.slice(0, 3)}) ${p.slice(3, 6)}-${p.slice(6)}` : p;

/** Admin → Rewards: manually credit or remove points for a customer by phone. */
export function PointsCreditCard({ token }) {
  const [phone, setPhone] = useState("");
  const [customer, setCustomer] = useState(null);
  const [mode, setMode] = useState("add");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const lookup = async () => {
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 7) {
      toast.error("Enter the customer's phone number");
      return;
    }
    setBusy(true);
    try {
      const [{ data }, hist] = await Promise.all([
        adminApi(token).get("/admin/loyalty/lookup", { params: { phone: digits } }),
        adminApi(token).get("/admin/loyalty/adjustments", { params: { phone: digits } }),
      ]);
      setCustomer(data);
      setHistory(hist.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Lookup failed");
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    const n = parseInt(amount, 10);
    if (!n || n <= 0) {
      toast.error("Enter how many points");
      return;
    }
    setBusy(true);
    try {
      const { data } = await adminApi(token).post("/admin/loyalty/adjust", {
        phone: customer.phone,
        points: mode === "add" ? n : -n,
        reason,
        customer_note: mode === "add" ? note : "",
      });
      setCustomer((c) => ({ ...c, exists: true, points: data.points }));
      setHistory((h) => [
        { points: mode === "add" ? n : -n, reason, balance_after: data.points, created_at: new Date().toISOString() },
        ...h,
      ]);
      setAmount("");
      setReason("");
      setNote("");
      toast.success(
        mode === "add"
          ? `Added ${n} points — new balance ${data.points}`
          : `Removed ${n} points — new balance ${data.points}`
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not adjust points");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white rounded-2xl border border-stone-200 p-5 space-y-4" data-testid="points-credit-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-brand-yellow/20 flex items-center justify-center shrink-0">
          <Gift className="w-5 h-5 text-brand-red" />
        </div>
        <div>
          <div className="font-display text-brand-red text-xl leading-tight">Give Customer Points</div>
          <div className="text-xs text-stone-500">Make it right after a problem order — credit points to any phone number.</div>
        </div>
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); lookup(); }}
        className="flex gap-2"
      >
        <input
          type="tel"
          inputMode="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Customer phone number"
          data-testid="points-credit-phone-input"
          className="flex-1 px-3 py-2.5 rounded-xl border-2 border-stone-200 text-sm outline-none focus:border-brand-red"
        />
        <button
          type="submit"
          disabled={busy}
          data-testid="points-credit-lookup-btn"
          className="px-4 py-2.5 rounded-xl bg-stone-900 text-white text-xs font-bold uppercase tracking-widest flex items-center gap-1.5 disabled:opacity-50"
        >
          <Search className="w-3.5 h-3.5" /> Find
        </button>
      </form>

      {customer && (
        <div className="space-y-4" data-testid="points-credit-result">
          <div className="flex items-center justify-between rounded-xl bg-stone-50 border border-stone-200 px-4 py-3">
            <div className="min-w-0">
              <div className="text-sm font-bold truncate" data-testid="points-credit-customer-name">
                {customer.name || (customer.exists ? "Rewards member" : "Not signed up yet")}
              </div>
              <div className="text-xs text-stone-500">{fmtPhone(customer.phone)}</div>
            </div>
            <div className="text-right">
              <div className="font-display text-2xl text-brand-red leading-none" data-testid="points-credit-balance">{customer.points}</div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">points</div>
            </div>
          </div>
          {!customer.exists && (
            <div className="text-xs text-stone-500" data-testid="points-credit-new-account-note">
              No account yet — adding points will create one, and they'll see the balance the first time they sign in with this number.
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-full border-2 border-stone-200 overflow-hidden text-xs font-bold">
              <button
                type="button"
                onClick={() => setMode("add")}
                data-testid="points-credit-mode-add"
                className={`px-3 py-1.5 flex items-center gap-1 ${mode === "add" ? "bg-emerald-600 text-white" : "bg-white text-stone-600"}`}
              >
                <Plus className="w-3 h-3" /> Add
              </button>
              <button
                type="button"
                onClick={() => setMode("remove")}
                data-testid="points-credit-mode-remove"
                className={`px-3 py-1.5 flex items-center gap-1 ${mode === "remove" ? "bg-red-600 text-white" : "bg-white text-stone-600"}`}
              >
                <Minus className="w-3 h-3" /> Remove
              </button>
            </div>
            {QUICK.map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => setAmount(String(q))}
                data-testid={`points-credit-quick-${q}`}
                className={`px-3 py-1.5 rounded-full border-2 text-xs font-bold ${amount === String(q) ? "border-brand-red text-brand-red" : "border-stone-200 text-stone-600"}`}
              >
                {q}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-[110px_1fr] gap-2">
            <input
              type="number"
              min="1"
              inputMode="numeric"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Points"
              data-testid="points-credit-amount-input"
              className="px-3 py-2.5 rounded-xl border-2 border-stone-200 text-sm font-bold outline-none focus:border-brand-red"
            />
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 200))}
              placeholder="Internal note (optional) — e.g. BK-12345 missing fries"
              data-testid="points-credit-reason-input"
              className="px-3 py-2.5 rounded-xl border-2 border-stone-200 text-sm outline-none focus:border-brand-red"
            />
          </div>
          {mode === "add" && (
            <div className="space-y-1">
              <input
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 200))}
                placeholder="Message the customer will see (optional) — e.g. Sorry about the wait!"
                data-testid="points-credit-note-input"
                className="w-full px-3 py-2.5 rounded-xl border-2 border-brand-yellow/60 bg-brand-yellow/10 text-sm outline-none focus:border-brand-red"
              />
              <div className="text-[11px] text-stone-500">
                They'll see a "We added points" notice with this message the next time they open Rewards with their number.
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={apply}
            disabled={busy || !amount}
            data-testid="points-credit-apply-btn"
            className={`w-full py-3 rounded-full text-white text-xs font-black uppercase tracking-widest disabled:opacity-40 ${mode === "add" ? "bg-emerald-600" : "bg-red-600"}`}
          >
            {busy ? "Working…" : mode === "add" ? `Add ${amount || ""} points` : `Remove ${amount || ""} points`}
          </button>

          {history.length > 0 && (
            <div className="space-y-1" data-testid="points-credit-history">
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Recent adjustments</div>
              {history.slice(0, 6).map((h, i) => (
                <div key={h.id || i} className="flex items-center justify-between text-xs py-1.5 border-b border-stone-100 last:border-0">
                  <div className="min-w-0">
                    <span className={`font-bold ${h.points > 0 ? "text-emerald-700" : "text-red-700"}`}>
                      {h.points > 0 ? `+${h.points}` : h.points}
                    </span>
                    {h.reason && <span className="text-stone-500 ml-2 truncate">{h.reason}</span>}
                  </div>
                  <div className="text-stone-400 shrink-0 ml-3">
                    {new Date(h.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })} · bal {h.balance_after}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
